import axios from 'axios';
import authService from './authService';
import { getApiUrl } from '../utils/runtimeConfig';
import { resolveTenantSlug } from '../hooks/useTenant';
import { buildLoginPath } from '../utils/tenantResolutionStore';
import {
  SESSION_EXPIRED_STORAGE_KEY,
  SESSION_EXPIRED_MESSAGE,
  SESSION_ABGEMELDET_MESSAGE
} from '../utils/errorHandling';

const apiClient = axios.create({
  baseURL: getApiUrl(),
  headers: {
    'Content-Type': 'application/json'
  }
});

// Patterns for which a 401 must NOT trigger a logout redirect.
// Each entry may carry an optional `method` to restrict the skip to a specific
// HTTP verb; omitting `method` matches all verbs for that URL pattern.
// `exact: true` compares the path portion (without query string) via endsWith
// to prevent sub-path over-matching (e.g. DELETE /users/me must not match
// DELETE /users/me/profile-picture).
//
// '/users/me/password' PUT  — wrong-password response handled inline by the
//                            ChangePasswordModal; global redirect must not fire.
// '/users/me'          DELETE — wrong-password confirmation handled inline by
//                            DeleteAccountModal; global redirect must not fire.
// '/auth-config'            — bootstrap call; 401 must not trigger a loop.
// '/public/tenant/by-hostname' — bootstrap call; 401 must not trigger a loop.
const SKIP_LOGOUT_PATTERNS = [
  { method: 'PUT',    pattern: '/users/me/password' },
  { method: 'DELETE', pattern: '/users/me', exact: true },
  { pattern: '/auth-config' },
  { pattern: '/public/tenant/by-hostname' },
];

apiClient.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url ?? '';
    const requestMethod = (error.config?.method || '').toUpperCase();
    const requestPath = requestUrl.split('?')[0];
    const skipLogout = SKIP_LOGOUT_PATTERNS.some(({ method, pattern, exact }) => {
      const matches = exact
        ? (requestPath === pattern || requestPath.endsWith(pattern))
        : requestUrl.includes(pattern);
      return matches && (!method || method === requestMethod);
    });

    if (error.response?.status === 401 && !skipLogout) {
      const errorCode = error.response?.data?.error;

      const sessionMessage = errorCode === 'SESSION_EXPIRED'
        ? SESSION_EXPIRED_MESSAGE
        : SESSION_ABGEMELDET_MESSAGE;

      sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, sessionMessage);

      // Flag the error so calling components can skip showing generic messages
      error.isSessionExpired = true;

      authService.logout();

      // In matched-hostname mode the slug is NOT present in the URL path, so
      // resolveTenantSlug() would wrongly read the first page segment as slug.
      // buildLoginPath() returns '/login' when hostname-matched, null otherwise.
      const matchedLoginPath = buildLoginPath();
      if (matchedLoginPath) {
        window.location.href = matchedLoginPath;
      } else {
        const tenantSlug = resolveTenantSlug();
        window.location.href = `/${tenantSlug}/login`;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
